import Matter from "matter-js";
import { EventBus } from "../../core/event-bus.js";
import * as C from "../../config/constants.js";
import { MERGE_FLASH_MS } from "./constants.js";
import { markFlaskCargoMeta, spawnMergeEffect } from "./bodies.js";
import { onFlaskBodySpawned } from "./overflow-state.js";
import { requestContainmentBurst } from "./containment.js";

function getMergeMotionMs() {
  var v = C.MERGE_MOTION_MS != null ? Number(C.MERGE_MOTION_MS) : 500;
  return isFinite(v) && v > 0 ? v : 500;
}

/** Merging animals are kinematic — disable collisions so the pile does not stick to them. */
function disableMergeMotionCollisions(body) {
  if (!body || body._mergeMotionSavedFilter) return;
  var prev = body.collisionFilter;
  body._mergeMotionSavedFilter = prev
    ? { category: prev.category, mask: prev.mask }
    : { category: 0x0001, mask: 0xffffffff };
  var off = { category: 0, mask: 0 };
  body.collisionFilter = off;
  Matter.Body.set(body, { collisionFilter: off });
}

function isBodyInMerge(system, bodyId) {
  var list = system.activeMergeMotions;
  if (!list || !list.length) return false;
  for (var i = 0; i < list.length; i++) {
    var anim = list[i];
    if (anim.a.id === bodyId || anim.b.id === bodyId) return true;
  }
  return false;
}

export function isBodyMerging(system, bodyId) {
  return isBodyInMerge(system, bodyId);
}

/**
 * @param {object} system FlaskSystem
 * @param {object} m pending merge payload from collisions
 */
export function queueMergeMotion(system, m) {
  if (!system || !m || !m.a || !m.b) return;
  if (isBodyMerging(system, m.a.id) || isBodyMerging(system, m.b.id)) return;
  if (!system.bodiesMap[m.a.id] || !system.bodiesMap[m.b.id]) return;

  system.activeMergeMotions = system.activeMergeMotions || [];
  Matter.Body.setStatic(m.a, true);
  Matter.Body.setStatic(m.b, true);
  disableMergeMotionCollisions(m.a);
  disableMergeMotionCollisions(m.b);
  Matter.Body.setVelocity(m.a, { x: 0, y: 0 });
  Matter.Body.setVelocity(m.b, { x: 0, y: 0 });

  system.activeMergeMotions.push({
    a: m.a,
    b: m.b,
    metaA: m.metaA,
    metaB: m.metaB,
    tier: m.tier,
    nextTier: m.nextTier,
    canUpgradeTier: m.canUpgradeTier,
    mx: m.mx,
    my: m.my,
    startAx: m.a.position.x,
    startAy: m.a.position.y,
    startBx: m.b.position.x,
    startBy: m.b.position.y,
    startMs: Date.now(),
    durationMs: getMergeMotionMs(),
  });
  var radii = system.bodyRadiusByTier || [];
  var rA = radii[m.metaA.tier] != null ? radii[m.metaA.tier] : 10;
  var rB = radii[m.metaB.tier] != null ? radii[m.metaB.tier] : 10;
  var resultTier = m.canUpgradeTier ? m.nextTier : m.tier;
  var resultRadius = radii[resultTier] != null ? radii[resultTier] : (rA + rB) * 0.5;
  spawnMergeEffect(system, m.mx, m.my, m.tier, { bodyRadius: resultRadius });
}

function finishMergeMotion(system, anim) {
  var World = Matter.World;
  var a = anim.a;
  var b = anim.b;
  if (!system.bodiesMap[a.id] || !system.bodiesMap[b.id]) return;

  World.remove(system.world, a);
  World.remove(system.world, b);
  delete system.bodiesMap[a.id];
  delete system.bodiesMap[b.id];
  system.bodies = system.bodies.filter(function (x) {
    return x.body !== a && x.body !== b;
  });

  if (anim.canUpgradeTier) {
    var newMeta = system.createBody(anim.nextTier, anim.mx, anim.my);
    if (newMeta) {
      onFlaskBodySpawned(newMeta);
      World.add(system.world, newMeta.body);
      system.bodies.push(newMeta);
      system.bodiesMap[newMeta.body.id] = newMeta;
      markFlaskCargoMeta(newMeta);
      if (typeof system.opts.onUnlockTier === "function") system.opts.onUnlockTier(anim.nextTier);
      var fromName = system.evolutionChain[anim.tier].name;
      var toName = system.evolutionChain[anim.nextTier].name;
      if (EventBus && EventBus.emit) {
        EventBus.emit("flask:merge", {
          fromTier: anim.tier,
          toTier: anim.nextTier,
          fromName: fromName,
          toName: toName,
          position: { x: anim.mx, y: anim.my },
        });
      }
    }
  } else {
    if (EventBus && EventBus.emit) {
      var cappedName = system.evolutionChain[anim.tier]
        ? system.evolutionChain[anim.tier].name
        : "Unknown";
      EventBus.emit("flask:merge", {
        fromTier: anim.tier,
        toTier: null,
        fromName: cappedName,
        toName: null,
        capped: true,
        position: { x: anim.mx, y: anim.my },
      });
    }
  }

  system.mergeFlashUntil = Date.now() + MERGE_FLASH_MS;
  system.mergeFlashPos = { x: anim.mx, y: anim.my };
  requestContainmentBurst(system);
}

export function updateMergeMotions(system) {
  if (!system || !system.activeMergeMotions || !system.activeMergeMotions.length) {
    system.mergeMotionDraw = null;
    return;
  }
  var now = Date.now();
  var draw = {};
  var remaining = [];

  for (var i = 0; i < system.activeMergeMotions.length; i++) {
    var anim = system.activeMergeMotions[i];
    if (!system.bodiesMap[anim.a.id] || !system.bodiesMap[anim.b.id]) continue;

    var elapsed = now - anim.startMs;
    var t = Math.min(1, elapsed / anim.durationMs);
    var attractT = t < 0.42 ? t / 0.42 : 1;
    var ax = anim.startAx + (anim.mx - anim.startAx) * attractT;
    var ay = anim.startAy + (anim.my - anim.startAy) * attractT;
    var bx = anim.startBx + (anim.mx - anim.startBx) * attractT;
    var by = anim.startBy + (anim.my - anim.startBy) * attractT;
    Matter.Body.setPosition(anim.a, { x: ax, y: ay });
    Matter.Body.setPosition(anim.b, { x: bx, y: by });

    var growT = t < 0.42 ? 0 : (t - 0.42) / 0.58;
    var scale = 1 + growT * 0.35;
    var alpha = t > 0.76 ? 1 - (t - 0.76) / 0.24 : 1;
    draw[anim.a.id] = { scale: scale, alpha: alpha };
    draw[anim.b.id] = { scale: scale, alpha: alpha };

    if (t >= 1) {
      finishMergeMotion(system, anim);
    } else {
      remaining.push(anim);
    }
  }

  system.activeMergeMotions = remaining;
  system.mergeMotionDraw = draw;
}